import { ArrowUpRight, Boxes, BriefcaseBusiness, Building2, CalendarDays, FileText, MapPin, ShieldCheck, Target } from 'lucide-react';
import { Link } from 'react-router';
import { SectionHeading } from '../lib/DashboardKit.tsx';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Card, CardBody, Tabs } from '@crm/ui';
import { useEntities, useSites } from '../admin/data.ts';
import { useTenant } from '../tenant/TenantProvider.tsx';
import { HomeDashboard, type HomePeriod, type HomeVisitScope } from './HomeDashboard.tsx';

function Tile({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <Card className="border-slate-200/80 bg-gradient-to-br from-white to-slate-50/70">
      <CardBody className="flex gap-4">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-700">{icon}</span>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
          <div className="mt-1 text-sm text-slate-900">{children}</div>
        </div>
      </CardBody>
    </Card>
  );
}

export function HomePage() {
  const { t } = useTranslation();
  const { me, context, can } = useTenant();
  const [tab, setTab] = useState('dashboard');
  const [period, setPeriod] = useState<HomePeriod>('12-mois');
  const [visitScope, setVisitScope] = useState<HomeVisitScope>('team');
  // Ces clés sont partagées avec l'administration : une seule forme de cache (tableau aplati)
  // évite le plantage des modules ouverts après l'accueil.
  const entities = useEntities();
  const sites = useSites();
  const firstName = me.full_name.split(' ')[0];
  const actions = [
    { permission: 'accounts.read', to: '/comptes', icon: Building2, title: 'homeAccounts', hint: 'homeAccountsHint' },
    { permission: 'visits.read', to: '/visites', icon: CalendarDays, title: 'homeVisits', hint: 'homeVisitsHint' },
    { permission: 'pipeline.read', to: '/pipeline', icon: BriefcaseBusiness, title: 'homePipeline', hint: 'homePipelineHint' },
    { permission: 'sales.read', to: '/ventes/devis', icon: FileText, title: 'homeSales', hint: 'homeSalesHint' },
    { permission: 'stock.read', to: '/stock', icon: Boxes, title: 'homeStock', hint: 'homeStockHint' },
    { permission: 'targets.read', to: '/objectifs', icon: Target, title: 'homePerformance', hint: 'homePerformanceHint' },
  ].filter((item) => item.to === '/objectifs' ? ['targets.read', 'team_performance.read', 'targets.write', 'forecasts.write', 'forecasts.approve'].some(can) : can(item.permission));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="min-w-0"><h1 className="font-heading text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl" data-testid="hello">
          {t('home.hello', { name: firstName })}
        </h1>
        <p className="mt-1 hidden text-sm text-slate-600 sm:block">
          {t('home.intro', { product: context.tenant.theme.product_name || 'CRM Agro', tenant: context.tenant.name })}
        </p></div>
        <span className="hidden max-w-64 truncate rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 sm:inline-flex">{context.tenant.name}</span>
      </div>

      <div className="[&_[role=tab][data-state=active]]:border-slate-800 [&_[role=tab][data-state=active]]:text-slate-950 [&_[role=tab]]:focus-visible:ring-slate-900"><Tabs value={tab} onValueChange={setTab} label={t('homeDashboard.tabs')} tabs={[
        { value: 'dashboard', label: t('homeDashboard.dashboardTab'), testId: 'home-dashboard-tab' },
        { value: 'workspace', label: t('homeDashboard.workspaceTab'), testId: 'home-workspace-tab' },
      ]}>
        {tab === 'dashboard' ? <HomeDashboard period={period} setPeriod={setPeriod} visitScope={visitScope} setVisitScope={setVisitScope} /> : <section className="space-y-4" data-testid="home-workspace">
          <SectionHeading title={t('visuals.homeActions')} description={t('visuals.homeActionsHint')} />
          {actions.length > 0 && <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{actions.map(({ to, icon: Icon, title, hint }) => <Link key={to} to={to} className="group flex items-start gap-4 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm transition-[border-color,box-shadow] hover:border-slate-400 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-700"><Icon size={21} aria-hidden /></span>
            <div className="min-w-0 flex-1"><h3 className="font-semibold text-slate-900 group-hover:text-slate-700">{t(`visuals.${title}`)}</h3><p className="mt-1 text-sm leading-relaxed text-slate-600">{t(`visuals.${hint}`)}</p></div><ArrowUpRight size={17} className="shrink-0 text-slate-400 group-hover:text-slate-800" aria-hidden />
          </Link>)}</div>}
        </section>}
      </Tabs></div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile icon={<ShieldCheck size={20} />} label={t('home.role')}>
          <span className="font-semibold">{context.role.name}</span>
          {context.extra_roles.length ? (
            <div className="mt-1 flex flex-wrap gap-1">
              {context.extra_roles.map((r) => (
                <Badge key={r.code} tone="warning">{r.name}{r.valid_to ? ` → ${r.valid_to}` : ''}</Badge>
              ))}
            </div>
          ) : null}
        </Tile>
        <Tile icon={<Target size={20} />} label={t('home.scope')}>
          {context.scopes.length ? context.scopes.map((s) => <div key={s.id}>{s.name}</div>) : t('home.scopeAll')}
        </Tile>
        {can('entities.read') ? (
          <Tile icon={<Building2 size={20} />} label={t('home.entities')}>
            <span className="text-2xl font-bold text-slate-900" data-testid="entities-count">{entities.data?.length ?? '…'}</span>
          </Tile>
        ) : null}
        {can('sites.read') ? (
          <Tile icon={<MapPin size={20} />} label={t('home.sites')}>
            <span className="text-2xl font-bold text-slate-900">{sites.data?.length ?? '…'}</span>
          </Tile>
        ) : null}
      </div>

    </div>
  );
}
